<script setup>
import { CircleHelp, LogOut, MessageSquare, Settings } from 'lucide-vue-next'
import Button from 'primevue/button'
import { useRoute } from 'vue-router'
import { mainNavigation } from '@/config/navigation'
import { useCurrentUser } from '@/composables/useCurrentUser'
import { createAppVersionResource } from '@/services/app'
import { sessionStore } from '@/stores/session'
import { openFeedbackDialog } from '@/composables/feedback'

const route = useRoute()
const { profile, name, initials } = useCurrentUser()
const session = sessionStore()
const versionResource = createAppVersionResource()

function isSidebarActive(item) {
  return route.matched.some((record) => record.meta.sidebarGroup === item.name)
}

function logout() {
  session.logout.submit()
}
</script>

<template>
  <aside class="desktop-sidebar">
    <div class="brand">
      <img
        class="brand-logo"
        src="/retiora_icon/android-192.png"
        alt="Retiora"
      />

      <div class="brand-content">
        <div class="brand-name">Retiora</div>
        <div class="brand-subtitle">{{ $t('common.learningPlatform') }}</div>
      </div>
    </div>

    <nav class="sidebar-nav">
      <RouterLink
        v-for="item in mainNavigation"
        :key="item.name"
        :to="{ name: item.name }"
        class="sidebar-link"
        :class="{ 'sidebar-link--active': isSidebarActive(item) }"
      >
        <component
          :is="item.icon"
          :size="21"
          :stroke-width="1.8"
        />
        <span>{{ $t(`navigation.${item.name}`) }}</span>
      </RouterLink>
    </nav>

    <div class="sidebar-bottom">
      <RouterLink :to="{ name: 'getting-started' }" class="sidebar-link">
        <CircleHelp :size="21" :stroke-width="1.8" />
        <span>{{ $t('navigation.gettingStarted') }}</span>
      </RouterLink>
      <RouterLink
        :to="{ name: 'settings' }"
        class="sidebar-link"
      >
        <Settings
          :size="21"
          :stroke-width="1.8"
        />
        <span>{{ $t('navigation.settings') }}</span>
      </RouterLink>

      <details class="user-menu">
        <summary class="user-card">
          <div class="avatar">
            <img
              v-if="profile.user_image"
              :src="profile.user_image"
              :alt="name"
            />
            <span v-else>{{ initials }}</span>
          </div>

          <div>
            <div class="user-name">{{ name }}</div>
            <div class="user-role">{{ profile.role || $t('common.student') }}</div>
            <div
              v-if="versionResource.data?.version"
              class="app-version"
            >
              {{ versionResource.data.version }}
            </div>
          </div>
        </summary>

        <div class="user-menu-dropdown">
          <Button
            type="button"
            class="user-menu-item"
            unstyled
            @click="openFeedbackDialog"
          >
            <MessageSquare :size="17" />
            <span>{{ $t('feedback.sendFeedback') }}</span>
          </Button>
          <Button
            type="button"
            class="user-menu-item"
            unstyled
            :disabled="session.logout.loading"
            @click="logout"
          >
            <LogOut :size="17" />
            <span>{{ $t('common.logout') }}</span>
          </Button>
        </div>
      </details>
    </div>
  </aside>
</template>

<style scoped>
.desktop-sidebar {
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 30;
  box-sizing: border-box;
  width: 274px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--surface-card);
  border-right: 1px solid #dedee8;
}

.brand {
  height: 136px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 30px 17px;
}

.brand-logo {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 9px;
}

.brand-name {
  margin-top: -2px;
  color: #3d37c9;
  font-size: 26px;
  line-height: 1.1;
  font-weight: 700;
}

.brand-subtitle {
  margin-top: 4px;
  color: #272735;
  font-size: 13px;
  letter-spacing: 0.3px;
}

.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sidebar-link {
  position: relative;
  box-sizing: border-box;
  height: 52px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 18px;
  color: #303142;
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  transition: 0.18s ease;
}

.sidebar-link:hover {
  background: #f5f5fb;
}

.sidebar-link.router-link-active,
.sidebar-link--active {
  color: #3832cf;
  background: #efefff;
  font-weight: 700;
}

.sidebar-link.router-link-active::after,
.sidebar-link--active::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 4px;
  height: 100%;
  border-radius: 4px 0 0 4px;
  background: #4038d4;
}

.sidebar-bottom {
  margin-top: auto;
}

.user-menu {
  position: relative;
}

.user-menu summary {
  list-style: none;
  cursor: pointer;
}

.user-menu summary::-webkit-details-marker {
  display: none;
}

.user-menu[open] .user-card,
.user-card:hover {
  background: #f5f5fb;
}

.user-card {
  box-sizing: border-box;
  height: 80px;
  margin-top: 16px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-top: 1px solid #eeeef3;
}

.user-menu-dropdown {
  position: absolute;
  right: 14px;
  bottom: calc(100% + 6px);
  z-index: 1;
  min-width: 160px;
  padding: 5px;
  background: var(--surface-card);
  border: 1px solid #dedee8;
  border-radius: 9px;
  box-shadow: 0 8px 24px rgb(44 43 93 / 14%);
}

.user-menu-item {
  width: 100%;
  padding: 9px 10px;
  display: flex;
  align-items: center;
  gap: 9px;
  color: #303142;
  background: transparent;
  border: 0;
  border-radius: 6px;
  font: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}

.user-menu-item:hover {
  background: #f5f5fb;
}

.user-menu-item:disabled {
  opacity: 0.6;
  cursor: wait;
}

.avatar {
  width: 38px;
  height: 38px;
  overflow: hidden;
  display: grid;
  place-items: center;
  color: #ffffff;
  border-radius: 50%;
  background: #5a55df;
  font-size: 13px;
  font-weight: 700;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
}

.user-role {
  margin-top: 2px;
  font-size: 12px;
  color: var(--text-secondary);
}

.app-version {
  margin-top: 3px;
  color: #9999aa;
  font-size: 10px;
  line-height: 1;
  letter-spacing: 0.02em;
}

@media (max-width: 1023px) {
  .desktop-sidebar {
    display: none;
  }
}
</style>
